
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  StarIcon, 
  MessageSquare, 
  Clock, 
  Award, 
  ThumbsUp, 
  MapPin, 
  BriefcaseIcon,
  Languages,
  GraduationCap,
  Heart,
  ExternalLink,
  Check
} from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn, getImageUrl } from "@/lib/utils";
import { API_END_POINTS, ROUTES } from "@/utils/routes";
import { authHeaders, Axios } from "@/utils/axios";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "@/store/userStore";

export interface FreelancerProps {
  _id: string;
  name: string;

  fullName?: string;
  designation?: string;
  bio?: string;
  projectsCompleted?: number;
  experience?: string | number;

  profilePicture: string;
  title: string;
  rating: number;
  reviews: any;
  dailyRate: number;
  location: string;
  skills: string[];
  completedJobs: number;
  successRate: number;
  description: string;
  availability: string | boolean;
  isVerified: boolean;
  yearsOfExperience?: number;
  englishLevel?: string;
  matchScore?: number;
  handleFreelancerDetail?:any
}

export const FreelancerCard = ({ 
  _id,
  name, 
  profilePicture, 
  title, 
  bio,
  designation,
  rating, 
  reviews,
  dailyRate, 
  location, 
  skills, 
  projectsCompleted,
  completedJobs, 
  successRate, 
  description,
  availability,
  isVerified,
  experience,
  yearsOfExperience,
  englishLevel,
  matchScore,
  showMatchScore,
  handleFreelancerDetail
}: FreelancerProps & { showMatchScore?: boolean }) => {
  const [saved, setSaved] = useState(false);
  const { toast } = useToast();

  const [isShowLoader, setIsShowLoader] = useState(false)
  const navigate = useNavigate();
  const { user } = useUserStore();

  const handleContactClick = () => {
    toast({
      title: "Message sent",
      description: `Your message to ${name} has been sent.`,
    });
  };

  const handleSaveClick = () => {
    setSaved(!saved);
    toast({
      title: saved ? "Freelancer removed" : "Freelancer saved",
      description: saved ? `${name} has been removed from your saved list.` : `${name} has been added to your saved list.`,
    });
  };

  const getAvailabilityColor = () => {
    switch (availability) {
      case "available":
        return "text-emerald-500 bg-emerald-50";
      case "next-week":
        return "text-amber-500 bg-amber-50";
      default:
        return "text-blue-500 bg-blue-50";
    }
  };

  const getMatchScoreBg = (score?: number) => {
    if (!score) return "";
    if (score >= 90) return "from-emerald-500 to-emerald-600";
    if (score >= 80) return "from-sky-500 to-sky-600";
    if (score >= 70) return "from-blue-500 to-blue-600";
    if (score >= 60) return "from-indigo-500 to-indigo-600";
    return "from-gray-500 to-gray-600";
  };

    const handleInitiateChats = async (id: string) => {
        if (!id) {
          toast({
            title: "No application selected",
            variant: "destructive",
          });
          return;
        }
    
        try {
          setIsShowLoader(true);
          const response = await Axios.post(
            API_END_POINTS.LIST_CONVERSATIONS,
            { otherUserId: id},
            authHeaders(user?.accessToken)
          );
          toast({
            title: "Chat initiated successfully!",
            variant: "success",
          });
          setIsShowLoader(false);
          navigate(
            `${ROUTES.MESSAGES}?conversationId=${response.data._id}`
          );
        } catch (error) {
          setIsShowLoader(false);
          console.error("Chat initiation error:", error);
          toast({
            title: "Failed to initiate chat",
            description: "Please try again later.",
            variant: "destructive",
          });
        }
      };

  return (
     <Card onClick={handleFreelancerDetail} className="group h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden bg-white border-gray-100 relative">
      {showMatchScore && matchScore && (
        <div className="absolute -right-12 top-6 rotate-45 z-20">
          <div className={`py-1 px-12 text-xs font-semibold text-white bg-gradient-to-r ${getMatchScoreBg(matchScore)}`}>
            {matchScore}% Match
          </div>
        </div>
      )}
      
      <CardHeader className="pb-4 relative">
        <div className="absolute top-0 right-0 left-0 h-24 bg-gradient-to-br from-sky-400/10 to-emerald-400/5 z-0"></div>
        
        <div className="flex items-start gap-4 relative z-10">
          <div className="relative">
            <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-white shadow-md group-hover:shadow-lg transition-all duration-300">
              <img 
                src={getImageUrl(profilePicture) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(name)} 
                alt={name} 
                className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" 
              />
            </div>
            {isVerified && (
              <div className="absolute -right-1 -bottom-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full p-1 shadow-md">
                <Award className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-800 group-hover:text-sky-700 transition-colors duration-300 truncate">{name? name?.charAt(0).toUpperCase() + name.split(" ")[0].slice(1) : name}</CardTitle>
              <div className="flex items-center space-x-1 bg-amber-50 px-2 py-0.5 rounded-full shrink-0 ml-2">
                {reviews?.length  ?<span className="text-xs font-medium text-amber-700">{reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length}</span> : "0"}
                <StarIcon className="ml-1 h-3.5 w-3.5 text-amber-500 fill-amber-500" />
              </div>
            </div>
            
            <CardDescription className="text-base text-gray-600 mt-0.5 font-medium truncate">{designation? designation?.charAt(0).toUpperCase() + designation?.slice(1) : designation}</CardDescription>
            
            <div className="flex items-center mt-1 text-gray-500 text-xs">
              <MapPin className="h-3 w-3 mr-1 text-gray-400 shrink-0" />
              <span className="truncate">{location}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pb-4 space-y-4">
        <p className="text-sm text-gray-600 line-clamp-2 h-10 overflow-hidden group-hover:line-clamp-3 transition-all duration-500">
          {bio}
        </p>
        
        <div className="flex flex-wrap gap-1.5">
          {skills.slice(0, 3).map((skill, index) => (
            <Badge 
              key={skill} 
              variant="outline" 
              className={cn(
                "bg-gray-50/80 hover:bg-sky-50 text-gray-700 hover:text-sky-700 border-gray-200 transition-colors duration-200 whitespace-nowrap",
                index % 3 === 0 && "hover:bg-violet-50 hover:text-violet-700",
                index % 3 === 1 && "hover:bg-emerald-50 hover:text-emerald-700",
                index % 3 === 2 && "hover:bg-amber-50 hover:text-amber-700"
              )}
            >
              {skill}
            </Badge>
          ))}
          
          {skills.length > 3 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="bg-sky-50/50 text-sky-700 hover:bg-sky-100 border-sky-200">
                    +{skills.length - 3}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="bg-white border border-gray-200 shadow-md">
                  <div className="flex flex-col gap-1 p-1 max-w-xs">
                    {skills.slice(3).map((skill) => (
                      <span key={skill} className="text-xs text-gray-700 flex items-center">
                        <Check className="h-3 w-3 mr-1 text-sky-500" />
                        {skill}
                      </span>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-1.5 pt-1">
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-gradient-to-br from-sky-50/50 to-sky-50/80 border border-sky-100/50 group-hover:border-sky-200 transition-colors duration-300">
            <span className="text-lg font-bold text-sky-600">${dailyRate}</span>
            <span className="text-xs text-gray-500">Hourly</span>
          </div>
          
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-gradient-to-br from-purple-50/50 to-purple-50/80 border border-purple-100/50 group-hover:border-purple-200 transition-colors duration-300">
            <span className="text-lg font-bold text-purple-600">{projectsCompleted}</span>
            <span className="text-xs text-gray-500">Jobs</span>
          </div>
          
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-gradient-to-br from-emerald-50/50 to-emerald-50/80 border border-emerald-100/50 group-hover:border-emerald-200 transition-colors duration-300">
            <span className="text-lg font-bold text-emerald-600">{successRate}%</span>
            <span className="text-xs text-gray-500">Success</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          {experience && (
            <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50/80 rounded-md p-1.5 pl-2">
              <BriefcaseIcon className="h-3.5 w-3.5 text-gray-500 shrink-0" />
              <span className="truncate">{experience}+ yrs exp.</span>
            </div>
          )}
          
          {englishLevel && (
            <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50/80 rounded-md p-1.5 pl-2">
              <Languages className="h-3.5 w-3.5 text-gray-500 shrink-0" />
              <span className="truncate">{englishLevel}</span>
            </div>
          )}
        </div>
      </CardContent>
      
      <div className={cn(
        "px-4 py-1.5 text-xs flex items-center justify-center font-medium w-full",
        getAvailabilityColor()
      )}>
        <Clock className="h-3 w-3 mr-1.5 shrink-0" />
        <span>
          {availability === true
            ? "Available now" 
            : availability === "next-week" 
              ? "Available next week" 
              : "Available next month"}
        </span>
      </div>
      
      <CardFooter className="flex gap-2 pt-4">
        <Button 
          onClick={() => handleInitiateChats(_id)}
          className="flex-1 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 shadow-md hover:shadow-sky-200/40 transition-all duration-300 group"
          disabled={isShowLoader}
        >
          <MessageSquare className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
          Contact
        </Button>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant={saved ? "default" : "outline"} 
                onClick={handleSaveClick}
                className={cn(
                  "w-10 px-0",
                  saved 
                    ? "bg-rose-100 text-rose-700 hover:bg-rose-200 border-rose-200" 
                    : "border-gray-200 text-gray-500 hover:text-rose-600 hover:border-rose-200"
                )}
              >
                {saved ? (
                  <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
                ) : (
                  <Heart className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{saved ? 'Remove from saved' : 'Save to favorites'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                className="w-10 px-0 border-gray-200 text-gray-500 hover:text-sky-600 hover:border-sky-200"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View full profile</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider> */}
      </CardFooter>
    </Card>
  );
};
